// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { Dismiss20Regular, TextCollapse20Regular, TextDescription20Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../libs/hooks/useChat';
import { useUserPreferences } from '../../../libs/hooks/useUserPreferences';
import { AlertType } from '../../../libs/models/AlertType';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import { ResponseStyle } from '../../../libs/models/UserPreferences';
import { ConflictError, PreconditionFailedError } from '../../../libs/services/BaseService';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { addAlert } from '../../../redux/features/app/appSlice';
import { ChatState } from '../../../redux/features/conversations/ChatState';
import { markRestyleFinished, markRestyleStarted } from '../../../redux/features/conversations/conversationsSlice';

const useClasses = makeStyles({
    button: {
        marginLeft: tokens.spacingHorizontalS,
    },
    popoverSurface: {
        maxWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    popoverHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
    },
    popoverText: {
        margin: 0,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        flex: 1,
    },
    popoverActions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

interface RestyleButtonProps {
    message: IChatMessage;
}

/**
 * Restyle toggle on bot messages (#1551). Offers "Visa kortare" or "Visa utförligare" depending
 * on the persisted <see cref="IChatMessage.style"/>; for legacy messages with no recorded style,
 * falls back to the inverse of the user's default response-style preference.
 *
 * Wraps a nudge popover (#1563) that auto-opens once after a restyle if the user has been
 * restyling consistently in one direction — suggesting they update their default verbosity
 * instead of clicking restyle every time.
 */
export const RestyleButton: React.FC<RestyleButtonProps> = ({ message }) => {
    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const chat = useChat();
    const { fetchPreferences, updateResponseStyle, dismissRestyleNudge } = useUserPreferences();

    const conversations = useAppSelector((state: RootState) => state.conversations.conversations);
    const userPreferences = useAppSelector((state: RootState) => state.app.userPreferences);
    const responseStylePreferenceEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.ResponseStylePreferenceEnabled,
    );
    const responseStyleNudgeEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.ResponseStyleNudgeEnabled,
    );

    const [nudgePopoverOpen, setNudgePopoverOpen] = useState(false);

    const chatId = message.chatId;
    const messageId = message.id ?? '';
    // The Record<string, ChatState> index signature claims conversations[chatId] always returns
    // a ChatState, but at runtime it's ChatState | undefined — a chat deleted while this button
    // is still mounted would otherwise crash on the restylingMessageIds read below. Use the same
    // runtime `in`-check that markRestyleFinished in conversationsSlice uses.
    if (!(chatId in conversations)) {
        return null;
    }
    const conversation: ChatState = conversations[chatId];
    const inFlight = !!conversation.restylingMessageIds?.includes(messageId);

    // Restyle (#1551) shares the response-style feature flag with #1550.
    if (!responseStylePreferenceEnabled) {
        return null;
    }

    // Determine which direction to offer:
    //  - If message.style is known → flip it.
    //  - If unknown (legacy) → flip user-default. This keeps the button useful for old chats.
    const knownOrFallbackStyle = message.style ?? userPreferences.responseStyle;
    const targetStyle = knownOrFallbackStyle === ResponseStyle.Brief ? ResponseStyle.Detailed : ResponseStyle.Brief;
    const labelKey = targetStyle === ResponseStyle.Brief ? 'chatHistory.restyleShorter' : 'chatHistory.restyleLonger';

    const onClick = async () => {
        if (!messageId || inFlight) {
            return;
        }
        dispatch(markRestyleStarted({ chatId, messageId }));
        try {
            await chat.restyleMessage(chatId, messageId, targetStyle);

            // Refresh preferences so the backend-computed nudge eligibility reflects the
            // counter that this restyle just bumped. Only the just-clicked button opens the
            // popover — other RestyleButtons in the same chat read the same Redux state but
            // their local nudgePopoverOpen stays false.
            if (responseStyleNudgeEnabled) {
                const refreshed = await fetchPreferences();
                if (refreshed?.shouldShowResponseStyleNudge && refreshed.suggestedStyle != null) {
                    setNudgePopoverOpen(true);
                }
            }
        } catch (e: unknown) {
            // 412 — optimistic-concurrency failure (#1566). Another tab/device wrote between
            // restyle's read and upsert; ask the user to refresh rather than retrying blindly.
            // 409 — sources backing this answer are gone from the index; user needs to re-ask.
            let messageKey = 'chatHistory.restyleError';
            if (e instanceof PreconditionFailedError) {
                messageKey = 'chatHistory.restyleConcurrentEdit';
            } else if (e instanceof ConflictError) {
                messageKey = 'chatHistory.restyleSourcesGone';
            }
            dispatch(
                addAlert({
                    message: t(messageKey),
                    type: AlertType.Error,
                }),
            );
        } finally {
            dispatch(markRestyleFinished({ chatId, messageId }));
        }
    };

    const suggestedStyle = userPreferences.suggestedStyle;
    const suggestionTextKey =
        suggestedStyle === ResponseStyle.Brief
            ? 'nudges.responseStyle.suggestionBrief'
            : 'nudges.responseStyle.suggestionDetailed';
    const suggestionButtonKey =
        suggestedStyle === ResponseStyle.Brief
            ? 'nudges.responseStyle.updateDefaultBrief'
            : 'nudges.responseStyle.updateDefaultDetailed';

    const onApplySuggestion = async () => {
        setNudgePopoverOpen(false);
        if (suggestedStyle == null) {
            return;
        }
        try {
            await updateResponseStyle(suggestedStyle);
        } catch {
            // Already logged + reverted by the hook. Surface a user-facing alert too.
            dispatch(
                addAlert({
                    message: t('chatHistory.restyleError'),
                    type: AlertType.Error,
                }),
            );
        }
    };

    const onDismissNudge = async () => {
        setNudgePopoverOpen(false);
        try {
            await dismissRestyleNudge();
        } catch {
            // Hook reverted Redux state; nothing to do. The popover stays closed
            // locally — re-opening on every render after a failed dismiss would
            // be worse than swallowing.
        }
    };

    const button = (
        <Button
            appearance="subtle"
            size="small"
            className={classes.button}
            disabled={inFlight || !messageId}
            icon={
                inFlight ? (
                    <Spinner size="extra-tiny" />
                ) : targetStyle === ResponseStyle.Brief ? (
                    <TextCollapse20Regular />
                ) : (
                    <TextDescription20Regular />
                )
            }
            onClick={() => {
                void onClick();
            }}
            title={t(inFlight ? 'chatHistory.restyleInProgress' : labelKey)}
        >
            {t(inFlight ? 'chatHistory.restyleInProgress' : labelKey)}
        </Button>
    );

    // No popover wrapper at all when the nudge feature flag is off — keeps the
    // DOM identical to the pre-#1563 button so the existing behavior is
    // untouched when the flag is rolled out gradually.
    if (!responseStyleNudgeEnabled) {
        return button;
    }

    return (
        <Popover
            open={nudgePopoverOpen}
            // Fluent UI's Popover opens on trigger click by default. The trigger IS the
            // restyle button, so without this filter every restyle click would also pop
            // the suggestion open — bypassing the backend eligibility gate entirely.
            // Only propagate close events here; opens are driven programmatically from
            // onClick after fetchPreferences confirms the nudge is eligible.
            onOpenChange={(_event, data) => {
                if (!data.open) {
                    setNudgePopoverOpen(false);
                }
            }}
            positioning={{ position: 'above' }}
            withArrow
        >
            <PopoverTrigger disableButtonEnhancement>{button}</PopoverTrigger>
            <PopoverSurface className={classes.popoverSurface}>
                <div className={classes.popoverHeader}>
                    <p className={classes.popoverText}>{t(suggestionTextKey)}</p>
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<Dismiss20Regular />}
                        aria-label={t('nudges.responseStyle.dismissAriaLabel')}
                        onClick={() => {
                            void onDismissNudge();
                        }}
                    />
                </div>
                <div className={classes.popoverActions}>
                    <Button
                        appearance="primary"
                        size="small"
                        onClick={() => {
                            void onApplySuggestion();
                        }}
                    >
                        {t(suggestionButtonKey)}
                    </Button>
                </div>
            </PopoverSurface>
        </Popover>
    );
};
